import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Route, Router, RouterModule } from '@angular/router';
import { isSinapsisTestBusiness } from '@core/constants/sinapsis-business.constant';
import { PermissionsService } from '@core/services/permissions.service';
import { filterRoutesByPermission } from '@core/utils/route-permissions.util';
import { CompanyService } from '@features/company/services/company.service';
import { FleetService } from '@features/company/views/fleets/services/fleets.service';

const BASIC_MAINTENANCE_PATH = 'basic-maintenance';
const ADVANCED_MAINTENANCE_PATH = 'advanced-maintenance';
const POWER_BI_PATH = 'power-bi';

@Component({
  selector: 'app-menu-sidenav',
  imports: [MatListModule, MatSidenavModule, MatToolbarModule, RouterModule, MatIconModule],
  templateUrl: './menu-sidenav.html',
  styleUrl: './menu-sidenav.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * @description
 * Menú lateral del dashboard. Alterna mantenimiento básico/avanzado
 * según la modalidad configurada en el negocio.
 */
export class MenuSidenav {
  private readonly router = inject(Router);
  private readonly companyService = inject(CompanyService);
  private readonly permissions = inject(PermissionsService);
  readonly fleetService = inject(FleetService);

  private readonly allMenuRoutes: Route[] =
    this.router.config
      .find((r) => r.path === '' && !!r.children && !r.redirectTo)
      ?.children?.filter((r) => !!r.path && !r.redirectTo && !r.data?.['hideInMenu']) ?? [];

  constructor() {
    // Si la modalidad cambia y el usuario está en la ruta incorrecta, redirige.
    // El negocio Sinapsis de pruebas conserva ambas rutas. La modalidad ya está
    // hidratada por `CompanyService` (cacheada al iniciar sesión).
    effect(() => {
      if (isSinapsisTestBusiness(this.companyService.business)) {
        return;
      }

      const advanced = this.companyService.advancedMaintenanceEnabled();
      const url = this.router.url;

      if (advanced && url.startsWith(`/${BASIC_MAINTENANCE_PATH}`)) {
        void this.router.navigateByUrl(`/${ADVANCED_MAINTENANCE_PATH}`);
        return;
      }

      if (!advanced && url.startsWith(`/${ADVANCED_MAINTENANCE_PATH}`)) {
        void this.router.navigateByUrl(`/${BASIC_MAINTENANCE_PATH}`);
      }
    });
  }

  /**
   * Rutas visibles en el menú.
   * Se descartan primero las que el perfil del usuario no puede ver y luego se
   * aplica la modalidad de mantenimiento del negocio.
   * Lee el signal del servicio para reaccionar al guardar en configuración.
   * En el negocio Sinapsis de pruebas se muestran ambas modalidades.
   * "Informes BI" es una excepción: solo se muestra con mantenimiento avanzado
   * habilitado, incluso para el negocio Sinapsis de pruebas.
   */
  readonly menuRoutes = computed<Route[]>(() => {
    const showBothModalities = isSinapsisTestBusiness(this.companyService.business);
    const advanced = this.companyService.advancedMaintenanceEnabled();

    const allowedRoutes = filterRoutesByPermission(this.allMenuRoutes, (permission) =>
      this.permissions.has(permission),
    );

    return allowedRoutes.filter((route) => {
      // "Informes BI" solo está disponible para negocios con mantenimiento
      // avanzado habilitado, sin excepción para el negocio Sinapsis de pruebas.
      if (route.path === POWER_BI_PATH) {
        return advanced;
      }
      if (showBothModalities) {
        return true;
      }
      if (route.path === BASIC_MAINTENANCE_PATH) {
        return !advanced;
      }
      if (route.path === ADVANCED_MAINTENANCE_PATH) {
        return advanced;
      }
      return true;
    });
  });

  /**
   * @description
   * Obtiene la etiqueta visible del ítem de menú.
   * @param {Route} route Ruta del menú.
   * @returns {string}
   */
  getLabel(route: Route): string {
    return typeof route.title === 'string' ? route.title : (route.path ?? '');
  }
}
